import type { BaseListOptions } from '../types/base-list-options'
import type { PaginatedResponse } from '../types/paginated-response'

// Re-export for convenience
export type { BaseListOptions }
export type ListOptions = BaseListOptions

let BASE_URL = '/api'

export type Fetcher = (url: string, options: RequestInit) => Promise<Response>
export type GetAuthHeaders = () =>
  | Record<string, string>
  | Promise<Record<string, string>>

let fetcher: Fetcher = fetch
let getAuthHeaders: GetAuthHeaders | undefined

export function configure(options: {
  baseUrl?: string
  fetcher?: Fetcher
  getAuthHeaders?: GetAuthHeaders
}) {
  if (options.baseUrl !== undefined) {
    BASE_URL = options.baseUrl
  }
  if (options.fetcher !== undefined) {
    fetcher = options.fetcher
  }
  if (options.getAuthHeaders !== undefined) {
    getAuthHeaders = options.getAuthHeaders
  }
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  params?: Record<string, string | number | boolean | string[] | undefined>
  body?: any
}

export async function apiRequest<T>(
  path: string,
  options: RequestOptions = {}
): Promise<T> {
  const { params, body, ...fetchOptions } = options

  let url = `${BASE_URL}${path}`

  if (params) {
    const searchParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        // Rails expects array params as key[]=value1&key[]=value2
        value.forEach((v) => searchParams.append(key + '[]', v))
      } else if (value !== undefined && value !== null) {
        searchParams.append(key, String(value))
      }
    })
    const queryString = searchParams.toString()
    if (queryString) {
      url += `?${queryString}`
    }
  }

  const response = await fetcher(url, {
    ...fetchOptions,
    headers: {
      'Content-Type': 'application/json',
      ...fetchOptions.headers,
    },
    body: body ? JSON.stringify(body) : undefined,
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throw new ApiError(response.status, response.statusText, error)
  }

  const text = await response.text()
  return (text ? JSON.parse(text) : undefined) as T
}

export async function paginatedRequest<T>(
  path: string,
  options: RequestOptions = {}
): Promise<PaginatedResponse<T>> {
  const { params, body, ...fetchOptions } = options

  let url = `${BASE_URL}${path}`

  if (params) {
    const searchParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((v) => searchParams.append(key + '[]', v))
      } else if (value !== undefined && value !== null) {
        searchParams.append(key, String(value))
      }
    })
    const queryString = searchParams.toString()
    if (queryString) {
      url += `?${queryString}`
    }
  }

  const response = await fetcher(url, {
    ...fetchOptions,
    headers: {
      'Content-Type': 'application/json',
      ...fetchOptions.headers,
    },
    body: body ? JSON.stringify(body) : undefined,
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throw new ApiError(response.status, response.statusText, error)
  }

  const data = await response.json()

  // Extract pagination meta from response headers
  const meta = {
    total_count: parseInt(response.headers.get('total-count') || '0', 10),
    total_pages: parseInt(response.headers.get('total-pages') || '1', 10),
    current_page: parseInt(response.headers.get('current-page') || '1', 10),
    page_items: parseInt(
      response.headers.get('page-items') ||
        String(Array.isArray(data) ? data.length : 0),
      10
    ),
  }

  return { data: Array.isArray(data) ? data : [], meta }
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public data: any
  ) {
    super(`API Error: ${status} ${statusText}`)
    this.name = 'ApiError'
  }
}

// File upload helper for FormData requests
export async function uploadFile<T>(
  path: string,
  file: File | Blob,
  options: { keepName?: boolean } = {}
): Promise<T> {
  const formData = new FormData()
  formData.append('file', file)
  if (options.keepName) {
    formData.append('keep_name', 'true')
  }

  // Get auth headers if configured
  const authHeaders = getAuthHeaders ? await getAuthHeaders() : {}

  // Use native fetch for FormData to avoid Content-Type issues with custom fetchers
  // Don't set Content-Type header - browser will set it with boundary for FormData
  const response = await fetch(`${BASE_URL}${path}`, {
    method: 'POST',
    body: formData,
    headers: authHeaders,
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throw new ApiError(response.status, response.statusText, error)
  }

  return response.json()
}

// Bulk file upload helper
export async function uploadFiles<T>(
  path: string,
  files: (File | Blob)[]
): Promise<T> {
  const formData = new FormData()
  files.forEach((file) => formData.append('files[]', file))

  // Get auth headers if configured
  const authHeaders = getAuthHeaders ? await getAuthHeaders() : {}

  // Use native fetch for FormData to avoid Content-Type issues with custom fetchers
  const response = await fetch(`${BASE_URL}${path}`, {
    method: 'POST',
    body: formData,
    headers: authHeaders,
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throw new ApiError(response.status, response.statusText, error)
  }

  return response.json()
}
